import {FieldLegend, FieldSet} from '@kroll-resolver-crisp/ui-components-react';
import {type QcSubmissionAttributes} from '@/api/qcReview';
import {ClassificationScreenshots} from '@/components/classify/NoteScreenshots';

function DetailRow({label, children}: {readonly label: string; readonly children: string}) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="min-w-0 font-medium">{children}</span>
    </div>
  );
}

/**
 What the analyst submitted beyond the policy violation and primary risk - every risk, enrichment,
 example ids, notes and screenshots - so QC and QA reviewers judge the call with the analyst's context.
 */
export function AnalystSubmissionDetails({
  submission,
  classificationId
}: {
  readonly submission: QcSubmissionAttributes;
  readonly classificationId: string | null;
}) {
  const rows = [
    // The primary risk is already badged beside the policy violation; this only adds when there's more.
    ['Risks', submission.risks.length > 1 ? submission.risks : []],
    ['Language', submission.language],
    ['Location', submission.location],
    ['Topics', submission.topic],
    ['Core risk', submission.core_risk],
    ['Example ids', submission.example_ids ? [submission.example_ids] : []]
  ] as const;
  const shown = rows.filter(([, values]) => values.length > 0);

  return (
    <>
      {shown.length > 0 ? (
        <div className="flex flex-col gap-1">
          {shown.map(([label, values]) => (
            <DetailRow key={label} label={label}>
              {values.join(', ')}
            </DetailRow>
          ))}
        </div>
      ) : null}

      <FieldSet className="gap-0">
        <FieldLegend variant="label">Analyst notes</FieldLegend>
        <p className="bg-muted/50 p-2 text-xs whitespace-pre-wrap">
          {submission.notes || (
            <span className="text-muted-foreground">No notes from the analyst.</span>
          )}
        </p>
        <ClassificationScreenshots classificationId={classificationId} />
      </FieldSet>
    </>
  );
}
